'use client'

import { UserSingleSelect } from '@repo/ui/UserSingleSelect'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useClaimsStore } from '@repo/store/useClaimsStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import MUIButton from '@mui/material/Button'
import MUICheckbox from '@mui/material/Checkbox'
import MUILink from '@mui/material/Link'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITable from '@mui/material/Table'
import MUITableBody from '@mui/material/TableBody'
import MUITableCell from '@mui/material/TableCell'
import MUITableContainer from '@mui/material/TableContainer'
import MUITableHead from '@mui/material/TableHead'
import MUITableRow from '@mui/material/TableRow'
import MUITypography from '@mui/material/Typography'

type ClaimsPermissionsModuleProps = {
  isExpenses: boolean
}

type PermissionRow = {
  userId: string
  userName: string
  userEmail: string
  canApprove: boolean
  canProcessPayments: boolean
  permissionId?: string | null
}

type ApiPermission = {
  expenseUserPermissionId?: string
  overtimeUserPermissionId?: string
  processorUserId: string
  claimRecipientUserId: string
  canApprove: boolean
  canProcessPayments: boolean
}

export const ClaimsPermissionsModule = ({ isExpenses }: ClaimsPermissionsModuleProps) => {
  const { allUsers, fetchAllUsers } = useLookupsStore()
  const {
    createUpdateExpenseUserPermission,
    createUpdateOvertimeUserPermission,
    fetchAllExpenseUserPermissionsByProcessorUserId,
    fetchAllOvertimeUserPermissionsByProcessorUserId
  } = useClaimsStore()
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()

  const [selectedProcessorUserId, setSelectedProcessorUserId] = useState<string | null>(null)
  const [permissionRows, setPermissionRows] = useState<PermissionRow[]>([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!allUsers || (Array.isArray(allUsers) && allUsers.length === 0)) {
      fetchAllUsers()
    }
  }, [allUsers, fetchAllUsers])

  const normalizedUsers = useMemo(() => {
    const arr = Array.isArray(allUsers) ? allUsers : []
    return arr
      .map((u: any) => {
        const id = u?.userId ?? u?.bundleUserId ?? u?.id ?? null
        const name = u?.description ?? u?.name ?? u?.fullName ?? u?.displayName ?? String(id ?? '')
        const email = u?.emailAddress ?? u?.email ?? ''
        if (!id) return null
        return { id: String(id), name: String(name), email: String(email) }
      })
      .filter(Boolean) as Array<{ id: string; name: string; email: string }>
  }, [allUsers])

  const fetchPermissions = useCallback(async () => {
    if (!selectedProcessorUserId) return []
    if (isExpenses) {
      return fetchAllExpenseUserPermissionsByProcessorUserId({ processorUserId: selectedProcessorUserId })
    }
    return fetchAllOvertimeUserPermissionsByProcessorUserId({ processorUserId: selectedProcessorUserId })
  }, [
    isExpenses,
    fetchAllExpenseUserPermissionsByProcessorUserId,
    fetchAllOvertimeUserPermissionsByProcessorUserId,
    selectedProcessorUserId
  ])

  useEffect(() => {
    if (!selectedProcessorUserId) {
      setPermissionRows([])
      return
    }

    const buildRows = (permissions: ApiPermission[]) => {
      const permissionMap = new Map(permissions.map((p) => [String(p.claimRecipientUserId), p]))

      const rows: PermissionRow[] = normalizedUsers.map((user) => {
        const existing = permissionMap.get(user.id)
        return {
          userId: user.id,
          userName: user.name,
          userEmail: user.email,
          canApprove: existing?.canApprove ?? false,
          canProcessPayments: existing?.canProcessPayments ?? false,
          permissionId: isExpenses
            ? (existing?.expenseUserPermissionId ?? null)
            : (existing?.overtimeUserPermissionId ?? null)
        }
      })
      setPermissionRows(rows)
    }

    fetchPermissions().then(buildRows)
  }, [selectedProcessorUserId, normalizedUsers, fetchPermissions])

  const handleToggle = useCallback((userId: string, field: 'canApprove' | 'canProcessPayments') => {
    setPermissionRows((prev) => prev.map((row) => (row.userId === userId ? { ...row, [field]: !row[field] } : row)))
  }, [])

  const handleSelectAll = useCallback((field: 'canApprove' | 'canProcessPayments') => {
    setPermissionRows((prev) => {
      const allChecked = prev.every((row) => row[field])
      return prev.map((row) => ({ ...row, [field]: !allChecked }))
    })
  }, [])

  const handleSave = useCallback(async () => {
    if (!selectedProcessorUserId) return

    setSaving(true)
    try {
      const payload = permissionRows.map((row) => ({
        processorUserId: selectedProcessorUserId,
        claimRecipientUserId: row.userId,
        canApprove: row.canApprove,
        canProcessPayments: row.canProcessPayments,
        ...(isExpenses
          ? { expenseUserPermissionId: row.permissionId ?? 0 }
          : { overtimeUserPermissionId: row.permissionId ?? 0 })
      }))

      if (isExpenses) {
        await createUpdateExpenseUserPermission({ payload })
      } else {
        await createUpdateOvertimeUserPermission({ payload })
      }

      // Re-fetch to get updated IDs
      const updated = (await fetchPermissions()) as ApiPermission[]
      const permissionMap = new Map(updated.map((p: ApiPermission) => [String(p.claimRecipientUserId), p]))

      setPermissionRows((prev) =>
        prev.map((row) => {
          const existing = permissionMap.get(row.userId)
          return {
            ...row,
            permissionId: isExpenses
              ? (existing?.expenseUserPermissionId ?? null)
              : (existing?.overtimeUserPermissionId ?? null)
          }
        })
      )
    } catch (error) {
      console.error('Error saving permissions:', error)
    } finally {
      setSaving(false)
    }
  }, [selectedProcessorUserId, permissionRows, fetchPermissions, isExpenses, createUpdateExpenseUserPermission, createUpdateOvertimeUserPermission])

  return (
    <MUIPaper
      data-testid="ClaimsPermissionsModule"
      sx={{
        m: pageTitleBlockIsVisible ? '2.5rem auto' : '9.5rem auto 2.5rem',
        p: 6,
        maxWidth: '800px',
        width: '100%',
        backgroundColor: 'white'
      }}
    >
      <MUITypography variant="h5" sx={{ mb: 3 }}>
        {isExpenses ? 'Expense approval' : 'Overtime approval'}
      </MUITypography>

      <MUIStack direction="row" alignItems="flex-end" gap={2} sx={{ my: '20px' }}>
        <UserSingleSelect
          label="Processor user"
          value={selectedProcessorUserId}
          onChange={(userId: string | null) => setSelectedProcessorUserId(userId)}
          clearable
          size="small"
          sx={{ minWidth: '300px' }}
        />
        <MUIButton
          variant="contained"
          disabled={!selectedProcessorUserId || saving}
          onClick={handleSave}
          sx={{
            backgroundColor: '#377F86',
            '&:hover': { backgroundColor: '#2c666b' },
            textTransform: 'uppercase',
            height: 40
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </MUIButton>
      </MUIStack>

      {selectedProcessorUserId && permissionRows.length > 0 && (
        <MUITableContainer>
          <MUITable size="small">
            <MUITableHead>
              <MUITableRow>
                <MUITableCell sx={{ fontWeight: 'bold' }}>Claim recipient</MUITableCell>
                <MUITableCell sx={{ fontWeight: 'bold' }}>
                  Can approve claims{' '}
                  <MUILink
                    component="button"
                    variant="body2"
                    onClick={() => handleSelectAll('canApprove')}
                    sx={{ ml: 1 }}
                  >
                    Select all
                  </MUILink>
                </MUITableCell>
                <MUITableCell sx={{ fontWeight: 'bold' }}>
                  Can process payments{' '}
                  <MUILink
                    component="button"
                    variant="body2"
                    onClick={() => handleSelectAll('canProcessPayments')}
                    sx={{ ml: 1 }}
                  >
                    Select all
                  </MUILink>
                </MUITableCell>
              </MUITableRow>
            </MUITableHead>
            <MUITableBody>
              {permissionRows.map((row) => (
                <MUITableRow key={row.userId}>
                  <MUITableCell>
                    <MUITypography variant="body2" sx={{ mb: 0, fontWeight: 500 }}>
                      {row.userName}
                    </MUITypography>
                    <MUITypography variant="caption" color="text.secondary">
                      {row.userEmail}
                    </MUITypography>
                  </MUITableCell>
                  <MUITableCell>
                    <MUICheckbox
                      checked={row.canApprove}
                      onChange={() => handleToggle(row.userId, 'canApprove')}
                      size="small"
                    />
                  </MUITableCell>
                  <MUITableCell>
                    <MUICheckbox
                      checked={row.canProcessPayments}
                      onChange={() => handleToggle(row.userId, 'canProcessPayments')}
                      size="small"
                    />
                  </MUITableCell>
                </MUITableRow>
              ))}
            </MUITableBody>
          </MUITable>
        </MUITableContainer>
      )}
    </MUIPaper>
  )
}
